import { type RootState, type AppDispatch } from "features/Application/globaltypes/redux";
import { type FormikProps, withFormik } from "formik";
import { connect, type ConnectedProps } from "react-redux";
import { withAutosave, type AutosaveProps } from "utils/withAutosave";
import { type CampaignFormFields, type ConfigureProps } from "./types";
import { type CampaignPayload } from "../types/models";
import { noop } from "lodash";
import { bindAction } from "interfaces/redux";
import { saveCampaign } from "../state/slices/campaignDetailsSlice";
import { campaignFormFieldsSelector, campaignsStateSelector, normalizedTagsSelector } from "../state/selectors";
import CampaignForm from "./Forms/CampaignForm";
import { useEffect, useRef } from "react";
import { fetchCampaignOptions } from "../state/thunks/campaignDetailsThunk";
import { createCampaign, updateCampaign } from "../state/thunks/campaignEntityStateThunk";
import { getCampaignConfigureSchema } from "utils/validationSchemas/campaignValidationSchema";
import { fetchCampaignTags } from "../state/thunks/campaignTagsThunk";
import { PriorityLevels } from "enums";

export type ConfigurePropsAll = ConfigureProps & FormikProps<CampaignFormFields> & PropsFromRedux & AutosaveProps;

const validationSchema = getCampaignConfigureSchema();

const Configure = (props: ConfigurePropsAll) => {
  const isCampaignCreated = props.id > 0 || props.isCreating;

  const { isValid, onIsValidChange } = props;
  useEffect(() => {
    onIsValidChange?.(isValid);
  }, [isValid, onIsValidChange]);

  const values = useRef(props.values);
  useEffect(() => {
    values.current = props.values;
  }, [props.values]);

  const saveCampaign = () => props.saveCampaign(values.current);

  const onNext = () => {
    saveCampaign();
    if (!isCampaignCreated) {
      props.createCampaign(mapCampaignViewToPayload(values.current));
    }
  };

  useEffect(() => {
    props.acceptHandlers?.({
      onNext,
      onPrevious: saveCampaign,
    });
    props.fetchCampaignOptions();
    props.fetchCampaignTags();
    props.subscribeOnDiscarded?.(props.resetForm);
    return () => {
      props.unSubscribeOnDiscarded?.(props.resetForm);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <CampaignForm
      {...props}
      disablePreventTransitionPrompt={isCampaignCreated}
      types={props.campaignTypes}
      triggerTypes={props.triggerTypes}
      subdomains={props.subdomains}
      normalizedTags={props.normalizedTags}
      isReadOnlyView={props.isReadOnlyView}
    />
  );
};

const mapRemedialTraining = (remedialTraining: CampaignFormFields["remedialTraining"]) => {
  const { triggerTypeId, flowId, priority, fixedDueDate, isFixedDueDate, daysToComplete } = remedialTraining;

  const isRemedialTrainingSelected = !!triggerTypeId && !!flowId;
  const isRequiredPriority = isRemedialTrainingSelected && priority == PriorityLevels.required;

  return {
    triggerTypeId: triggerTypeId || null,
    flowId: !isRemedialTrainingSelected ? null : flowId,
    priority: !isRemedialTrainingSelected ? null : priority,
    dueDate: !isRequiredPriority || !isFixedDueDate ? null : fixedDueDate,
    daysToComplete: !isRequiredPriority || isFixedDueDate ? null : daysToComplete,
  };
};

const mapCampaignViewToPayload = (values: CampaignFormFields): CampaignPayload => {
  const remedialTraining = mapRemedialTraining(values.remedialTraining);
  return {
    id: values.id,
    name: values.name,
    campaignTypeId: values.campaignTypeId,
    subDomainId: values.subDomainId,
    labels: values.labels,
    remedialTraining,
  };
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState, ownProps: ConfigureProps) => {
  const base = campaignsStateSelector(state).base;

  return {
    id: ownProps.entityId || base.entityState.entityId,
    isCreating: base.entityState.changingEntityState,
    campaignFormFields: campaignFormFieldsSelector(state),
    isLoaded: base.details.isLoaded,
    isLoading: base.details.isLoading,
    isLoadingOptions: base.details.isLoadingOptions,
    isDraft: base.details.campaign.isDraft,
    campaignTypes: base.details.campaignTypes,
    triggerTypes: base.details.triggerTypes,
    subdomains: base.details.subdomains,
    normalizedTags: normalizedTagsSelector(state),
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  createCampaign: bindAction(createCampaign, dispatch),
  updateCampaign: bindAction(updateCampaign, dispatch),
  saveCampaign: bindAction(saveCampaign, dispatch),
  fetchCampaignOptions: bindAction(fetchCampaignOptions, dispatch),
  fetchCampaignTags: bindAction(fetchCampaignTags, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

/* istanbul ignore next */
const withAutosaveComponent = withAutosave<ConfigurePropsAll, CampaignFormFields, CampaignPayload>({
  getInitValues: (props) => mapCampaignViewToPayload(props.campaignFormFields),
  stateProvider: (values) => mapCampaignViewToPayload(values),
  entityUpdater: (props) => props.updateCampaign,
  isStateLoaded: (prev, props) => {
    const { isCreating, campaignFormFields } = props;
    const hasBeenLoadedIntoFormValues = campaignFormFields.id !== prev.campaignFormFields.id;

    return hasBeenLoadedIntoFormValues || (!isCreating && prev.isCreating);
  },
  isValid: (props) => props.isDraft && props.isValid && !props.isValidating && props.id > 0,
  isDeffered: (props) => !props.isLoaded,
})(Configure);

/* istanbul ignore next */
const component = withFormik({
  validationSchema,
  enableReinitialize: true,
  mapPropsToValues: (props: ConfigureProps & PropsFromRedux): CampaignFormFields => props.campaignFormFields,
  validateOnMount: true,
  handleSubmit: noop,
})(withAutosaveComponent);

const ConnectedComponent = connector(component);
export default ConnectedComponent;
